import { Alert, Box, Button, Typography } from "@mui/material";
import { API } from "aws-amplify";
import React from "react";
import { useUser } from "../contexts/useUser";
import CopyToClipboard from "react-copy-to-clipboard";
import { apiKeyManagementHeaders } from "../utils/apiKeyManagementAuth";

interface APIKeysProps {
  // Add any props you need for the component here
}

const APIKeys: React.FC<APIKeysProps> = () => {
  const { project } = useUser();
  const [apiKey1, setApiKey1] = React.useState("");
  const [apiKey2, setApiKey2] = React.useState("");
  const [key1Prefix, setKey1Prefix] = React.useState("");
  const [key2Prefix, setKey2Prefix] = React.useState("");
  const [key1Configured, setKey1Configured] = React.useState(false);
  const [key2Configured, setKey2Configured] = React.useState(false);
  const [revoke1FirstClick, setRevoke1FirstClick] = React.useState(false);
  const [revoke2FirstClick, setRevoke2FirstClick] = React.useState(false);
  const [key1LastRotated, setKey1LastRotated] = React.useState("");
  const [key2LastRotated, setKey2LastRotated] = React.useState("");

  const [showCopyWarning, setShowCopyWarning] = React.useState(false);

  /*
{
    key1: string;
    key1LastRotated : string;
    key2: string;
    key2LastRotated : string;
}
    */

  React.useEffect(() => {
    if (project?.id) {
      getAPIKeys();
    }
  }, [project?.id]);

  const applyKeyResponse = (keys: any, preservePlaintext = false) => {
    if (!preservePlaintext || keys.key1) setApiKey1(keys.key1 || "");
    if (!preservePlaintext || keys.key2) setApiKey2(keys.key2 || "");
    setKey1Configured(Boolean(keys.key1Configured || keys.key1));
    setKey2Configured(Boolean(keys.key2Configured || keys.key2));
    setKey1Prefix(keys.key1Prefix || "");
    setKey2Prefix(keys.key2Prefix || "");
    setKey1LastRotated(keys.key1LastRotated || "");
    setKey2LastRotated(keys.key2LastRotated || "");
  };

  const getAPIKeys = async () => {
    if (!project?.id) return;
    try {
      const response = await API.get("eventapi", "/getIntegrationApiKeys", {
        headers: await apiKeyManagementHeaders(),
        queryStringParameters: {
          projectId: project.id,
        },
      });
      if (response.success) {
        const keys = JSON.parse(response.body);
        applyKeyResponse(keys);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const createAPIKeys = async () => {
    const response = await API.post("eventapi", "/createIntegrationApiKeys", {
      headers: await apiKeyManagementHeaders(),
      body: {
        projectId: project?.id,
        projectName: project?.name,
      },
    });

    if (response.success) {
      const keys = JSON.parse(response.body);
      applyKeyResponse(keys);
      setShowCopyWarning(Boolean(keys.key1 || keys.key2));
    }
  };

  const rotateKey = async (keyname: string) => {
    try {
      const response = await API.post("eventapi", "/rotateIntegrationApiKey", {
        headers: await apiKeyManagementHeaders(),
        body: {
          projectId: project?.id,
          key: keyname,
        },
      });

      if (response.success) {
        const keys = JSON.parse(response.body);
        applyKeyResponse(keys, true);
        setShowCopyWarning(Boolean(keys[keyname]));
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Box
      p={2}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={2}
    >
      <Typography variant="h5" color="text.primary">
        API Keys for Customer API Access
      </Typography>
      <Typography variant="body2" color="text.primary">
        To make secure external calls via our API for Zapier or Stripe, you can
        use either of these API keys and the project id to authenticate.
      </Typography>

      {showCopyWarning && (
        <Alert severity="warning">
          Copy newly created or rotated keys now. For security, plaintext keys
          are not stored and cannot be shown again.
        </Alert>
      )}

      {!project && <Typography variant="body2">loading...</Typography>}

      {project && (
        <>
          {!key1Configured && !key2Configured && (
            <Button
              variant="contained"
              color="primary"
              onClick={async () => await createAPIKeys()}
            >
              Create API Keys for this Project
            </Button>
          )}

          <>
            {key1Configured && <Box display="flex" alignItems="flex-start">
              <Typography
                variant="body2"
                color="text.primary"
              >
                Key 1:{" "}
                {apiKey1 && apiKey1 !== ""
                  ? `${apiKey1.slice(0, 2)}${apiKey1
                      .slice(2, -2)
                      .replace(/./g, "*")}${apiKey1.slice(-2)}`
                  : `${key1Prefix || "configured"} (hidden)`}
                {key1LastRotated ? ` · rotated ${new Date(key1LastRotated).toLocaleString()}` : ""}
                &nbsp;&nbsp;
              </Typography>
              <CopyToClipboard text={apiKey1}>
                <Button
                  variant="contained"
                  disabled={!apiKey1 || apiKey1 === ""}
                >
                  Copy
                </Button>
              </CopyToClipboard>

              <Button
                variant={revoke1FirstClick ? "contained" : "text"}
                color="secondary"
                onClick={() => {
                  if (revoke1FirstClick) {
                    rotateKey("key1");
                  }
                  setRevoke1FirstClick(!revoke1FirstClick);
                }}
              >
                {revoke1FirstClick
                  ? "This action isn't reversible, confirm?"
                  : "revoke (rotate)"}
              </Button>
              {revoke1FirstClick && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setRevoke1FirstClick(false)}
                >
                  Cancel
                </Button>
              )}
            </Box>}
            {key2Configured && <Box display="flex" alignItems="flex-start">
              <Typography
                variant="body2"
                color="text.primary"
              >
                Key 2:{" "}
                {apiKey2 && apiKey2 !== ""
                  ? `${apiKey2.slice(0, 2)}${apiKey2
                      .slice(2, -2)
                      .replace(/./g, "*")}${apiKey2.slice(-2)}`
                  : `${key2Prefix || "configured"} (hidden)`}
                {key2LastRotated ? ` · rotated ${new Date(key2LastRotated).toLocaleString()}` : ""}
                &nbsp;&nbsp;
              </Typography>
              <CopyToClipboard text={apiKey2}>
                <Button
                  variant="contained"
                  disabled={!apiKey2 || apiKey2 === ""}
                >
                  Copy
                </Button>
              </CopyToClipboard>
              <Button
                variant={revoke2FirstClick ? "contained" : "text"}
                color="secondary"
                onClick={() => {
                  if (revoke2FirstClick) {
                    rotateKey("key2");
                  }
                  setRevoke2FirstClick(!revoke2FirstClick);
                }}
              >
                {revoke2FirstClick
                  ? "This action isn't reversible, confirm?"
                  : "revoke (rotate)"}
              </Button>
              {revoke2FirstClick && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={() => setRevoke2FirstClick(false)}
                >
                  Cancel
                </Button>
              )}
            </Box>}
          </>

          <Box display="flex" alignItems="flex-start">
            <Typography variant="body2">
              &nbsp;&nbsp; Project ID: {`${project?.id}`}
              &nbsp;&nbsp; &nbsp;&nbsp;
            </Typography>
            <CopyToClipboard text={project?.id ?? ""}>
              <Button variant="contained">Copy</Button>
            </CopyToClipboard>
          </Box>
        </>
      )}
    </Box>
  );
};

export default APIKeys;
